import { useCallback, useState } from "react";

export type OwnershipSelection = {
    selectedIds: string[];
    isSelected: (id: string) => boolean;
    toggle: (id: string) => void;
    toggleAll: (ids: string[]) => void;
    deselect: (id: string) => void;
    clear: () => void;
    areAllSelected: (ids: string[]) => boolean;
    areSomeSelected: (ids: string[]) => boolean;
};

export function useOwnershipSelection(): OwnershipSelection {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds]);

    const toggle = useCallback((id: string) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]));
    }, []);

    const toggleAll = useCallback((ids: string[]) => {
        setSelectedIds((prev) => {
            if (ids.length === 0) {
                return prev;
            }
            const allSelected = ids.every((id) => prev.includes(id));
            if (allSelected) {
                // Deselect only the current page/set — keep selections from other searches
                const removeSet = new Set(ids);
                return prev.filter((id) => !removeSet.has(id));
            }
            // Add current page/set without dropping earlier selections
            const next = new Set(prev);
            ids.forEach((id) => next.add(id));
            return [...next];
        });
    }, []);

    const deselect = useCallback((id: string) => {
        setSelectedIds((prev) => prev.filter((itemId) => itemId !== id));
    }, []);

    const clear = useCallback(() => setSelectedIds([]), []);

    const areAllSelected = useCallback(
        (ids: string[]) => ids.length > 0 && ids.every((id) => selectedIds.includes(id)),
        [selectedIds]
    );

    const areSomeSelected = useCallback(
        (ids: string[]) => ids.some((id) => selectedIds.includes(id)) && !areAllSelected(ids),
        [selectedIds, areAllSelected]
    );

    return {
        selectedIds,
        isSelected,
        toggle,
        toggleAll,
        deselect,
        clear,
        areAllSelected,
        areSomeSelected,
    };
}

export function buildTransferOwnershipPayload(selectedUser: {
    user?: { id?: string; email_address?: string };
}): { user_id?: string; email_address?: string } {
    const payload: { user_id?: string; email_address?: string } = {};

    if (selectedUser.user?.id) {
        payload.user_id = selectedUser.user.id;
    } else if (selectedUser.user?.email_address) {
        payload.email_address = selectedUser.user.email_address;
    } else {
        throw new Error("No valid user identifier found");
    }

    return payload;
}
